// How a plan's name and carrier are rendered to a human.
//
// Extracted from store/index.js so the client-facing AEP renewal wizard can
// reuse it. That step talks to oracle's /plan_search/ directly and never goes
// through the store's `parsePlan` pipeline, so it had been rendering raw
// catalog strings -- a Blue Cross MA Medigap client was shown "Medigap
// Supplement 1 Plan" while their card (and every agent-facing screen) says
// "Medex Bronze Plan". One copy of the rules, two callers.

// Long legal entity names -> the brand a beneficiary recognises.
export const CARRIERS_REPLACEMENTS = [
    [/AARP Medicare Supplement Plans, insured by UnitedHealthcare/i, 'UnitedHealthcare'],
    [/Humana Insurance Company/i, 'Humana'],
    [/BLUE CROSS AND BLUE SHIELD OF MASSACHUSETTS, INC./i, 'Blue Cross Blue Shield of Massachusetts'],
    [/FALLON HEALTH AND LIFE ASSURANCE CO. INC./i, 'Fallon Health'],
]

// Catalog plan names -> readable ones. This list is the LEGACY path: the
// intended replacement is `plan_overrides.preferred_name`, curated by agents
// editing the linked Product in Salesforce, which wins over anything here (see
// `planDisplayName`). It stays because overrides are sparse -- only a handful
// of plans in the whole catalog have one -- so removing it would regress every
// plan it still covers.
export const PLAN_NAME_REPLACEMENTS = [
    [/AARP Medicare Supplement Plans, insured by UnitedHealthcare/i, 'United AARP Supplemental'],
    [/Harvard Pilgrim Health Care Plan MA_SUPP2/i, 'Harvard Pilgrim Supplemental Plan 1A'],
    [/Harvard Pilgrim Health Care Plan MA_SUPP1/i, 'Harvard Pilgrim Supplemental Plan 1'],
    [/Harvard Pilgrim Health Care Plan MA_CORE/i, 'Harvard Pilgrim Supplemental Plan Core'],
    [/Harvard Pilgrim Health Care/i, 'Harvard Pilgrim'],
    [/MA_SUPP2/i, 'Plan 1A'],
    [/MA_SUPP_1A/i, 'Plan 1A'],
    [/MA_SUPP_2/i, 'Plan 1A'],
    [/MA_SUPP1/i, 'Plan 1'],
    [/MA_SUPP_1/i, 'Plan 1'],
    [/MA_CORE/i, 'Core'],
]

// Sequential .replace() over a [pattern, replacement] table. Non-strings pass
// through untouched rather than throwing, so a null carrier on a partial plan
// object can't take a page down.
export const applyReplacements = (str, table) => {
    if (typeof str !== 'string' || !str) { return str }
    return table.reduce((s, [pattern, replacement]) => s.replace(pattern, replacement), str)
}

export const carrierDisplayName = carrier => applyReplacements(carrier, CARRIERS_REPLACEMENTS)

// The name to show a human for `plan`.
//
// Precedence: the tenant's curated override, then the legacy regex table, then
// the raw catalog name. `preferredName` is passed explicitly by the store,
// which resolves overrides from the separate `plan_extras` block that /quotes2/
// ships; /plan_search/ instead carries `preferred_name` on the row itself, so
// that is the fallback. A blank override is treated as absent -- the oracle
// upsert path cannot clear one back to NULL, so '' is what a cleared override
// looks like in practice.
export const planDisplayName = (plan, preferredName) => {
    const preferred = preferredName ?? plan?.preferred_name
    if (typeof preferred === 'string' && preferred.trim()) { return preferred.trim() }
    // Falsy names pass through as-is (not coerced to ''), which is what the
    // store's getter has always done and what its callers expect.
    if (!plan?.name) { return plan?.name }
    return applyReplacements(plan.name, PLAN_NAME_REPLACEMENTS)
}

// The display name for a catalog carrier: trailing parentheticals that are just
// discount/rate markers are stripped ("(Standard)", "(15% Disc)", "(Standard
// 15% Disc)", "(10% Household Disc)") so the variants collapse into one option.
// A parenthetical that carries identity — "Humana (Humana Benefit Plan of
// Illinois, Inc.)" — is kept. Moved here from CurrentPlanStep.vue (#656) so the
// AEP builder's current-plan matcher (#721) applies the same rule.
export const baseCarrierName = (name) => {
    const m = /^(.*?)\s*\(([^)]*)\)$/.exec((name || '').trim())
    if (!m) { return (name || '').trim() }
    const discounty = /^(standard(\s+\d+%\s*(household\s+)?disc\.?)?|(\d+%\s*)?(household\s+)?disc\.?)$/i
    return discounty.test(m[2].trim()) ? baseCarrierName(m[1]) : (name || '').trim()
}

// The carrier without any of its parentheticals: "Medco Containment Life
// Insurance Company (Standard II)" -> "Medco Containment Life Insurance
// Company". A carrier plan note on this bare name covers every rate class of
// the company; oracle's get_plan_notes ships the notes by the same rule.
export const carrierCompanyName = carrier => (carrier || '').replace(/\s*\([^)]*\)/g, '').trim()

// Drop the carrier the catalog repeats at the front of every plan name.
// Compared word-wise on letters and digits only; requires two matching words
// so a plan that merely opens with the same generic one ("Blue ...") keeps
// its name. Two cursors, because pure-punctuation tokens key to '' and are
// dropped from carrierKeys (every carrier with a standalone dash).
export const stripCarrierPrefix = (name, carrier) => {
    const key = w => w.toLowerCase().replace(/[^a-z0-9]/g, '')
    const carrierKeys = (carrier || '').split(/\s+/).map(key).filter(Boolean)
    if (carrierKeys.length < 2) { return name }
    const nameWords = (name || '').split(/\s+/)
    let ci = 0
    let ni = 0
    let matched = 0
    while (ni < nameWords.length && ci < carrierKeys.length) {
        const k = key(nameWords[ni])
        if (!k) { ni += 1; continue }
        if (k !== carrierKeys[ci]) { break }
        ci += 1
        ni += 1
        matched += 1
    }
    if (matched < 2) { return name }
    return nameWords.slice(ni).join(' ').replace(/^[\s,:-]+/, '').trim()
}

// Catalog carrier rows grouped by display name (baseCarrierName), in
// first-appearance order: [{ name, variants: [raw carrier, ...] }]. One name
// in a picker, every variant behind it in the search. Shared by the
// current-plan step and the agent Plan Notes page.
export const groupCarrierVariants = (rows) => {
    const groups = []
    const byName = {}
    for (const row of rows || []) {
        const name = baseCarrierName(row.carrier)
        if (!byName[name]) {
            byName[name] = { name, variants: [] }
            groups.push(byName[name])
        }
        byName[name].variants.push(row.carrier)
    }
    return groups
}
